/**
 * MATLAB Language Support for CalcScript
 * Provides compatibility layer for MATLAB/Octave .m scripts
 */
// Export types (only from types.ts to avoid duplicates)
export { MatlabTokenType, MatlabLexerError, MatlabParseError, MatlabRuntimeError, MatlabError, } from "./types";
// Export lexer
export { MatlabLexer, matlabTokenize } from "./lexer";
// Export parser
export { MatlabParser, matlabParse } from "./parser";
// Export evaluator
export { MatlabEnvironment, MatlabEvaluator, matlabEvaluate, } from "./evaluator";
// Export builtins
export { matlabBuiltins, matlabConstants } from "./builtins";
// Export toolbox system
export { builtinToolboxes, getToolbox, getToolboxIds, getToolboxFunctionDoc, isToolboxFunction, } from "./toolboxes";
import { matlabParse } from "./parser";
import { matlabEvaluate, hoistFunctionDeclarations, MatlabEnvironment, } from "./evaluator";
import { MatlabError } from "./types";
// Plot metadata types returned by title(), xlabel(), ylabel(), etc.
// The second group is the figure/axes decoration commands: Quanta has no
// figure state to change, so they are absorbed rather than being treated as
// the statement's result.
const PLOT_METADATA_TYPES = new Set([
    "title",
    "xlabel",
    "ylabel",
    "zlabel",
    "xlim",
    "ylim",
    "axis",
    "grid",
    "legend",
    "axes",
    "box",
    "caxis",
    "clc",
    "close",
    "colormap",
    "drawnow",
    "figure",
    "hold",
    "set",
    "shading",
    "xline",
    "yline",
]);
export function isPlotValue(value) {
    return (typeof value === "object" &&
        value !== null &&
        "type" in value &&
        value.type === "plot");
}
export function isPlotMetadata(value) {
    return (typeof value === "object" &&
        value !== null &&
        "type" in value &&
        PLOT_METADATA_TYPES.has(value.type));
}
export function applyPlotMetadata(plot, meta) {
    switch (meta.type) {
        case "title":
            plot.title = meta.text;
            break;
        case "xlabel":
            plot.xlabel = meta.text;
            break;
        case "ylabel":
            plot.ylabel = meta.text;
            break;
        case "zlabel":
            plot.zlabel = meta.text;
            break;
        case "xlim":
        case "ylim":
        case "axis": {
            if (!plot.axisLimits)
                plot.axisLimits = {};
            const lim = meta.limits;
            if (meta.type === "xlim") {
                plot.axisLimits.xmin = lim[0];
                plot.axisLimits.xmax = lim[1];
            }
            else if (meta.type === "ylim") {
                plot.axisLimits.ymin = lim[0];
                plot.axisLimits.ymax = lim[1];
            }
            else if (meta.type === "axis") {
                if (!lim || lim.length === 0)
                    break;
                if (lim.length >= 2) {
                    plot.axisLimits.xmin = lim[0];
                    plot.axisLimits.xmax = lim[1];
                }
                if (lim.length >= 4) {
                    plot.axisLimits.ymin = lim[2];
                    plot.axisLimits.ymax = lim[3];
                }
            }
            break;
        }
    }
}
/**
 * Execute MATLAB code and return the result
 */
export function matlabExecute(code, env) {
    const execEnv = env || new MatlabEnvironment();
    const ast = matlabParse(code);
    // For multi-statement blocks, integrate plot metadata (title, xlabel, etc.)
    if (ast.type === "Block" && Array.isArray(ast.statements)) {
        const statements = ast.statements;
        let lastValue = null;
        let lastPlot = null;
        // Local functions are usually written below the code that calls them.
        hoistFunctionDeclarations(statements, execEnv);
        for (const stmt of statements) {
            const value = matlabEvaluate(stmt, execEnv);
            // A function declaration is not a result. Scripts that end with their
            // local functions would otherwise report a function value instead of
            // the plot or number they computed.
            if (stmt.type === "FunctionDeclaration")
                continue;
            // Merge plot metadata into the most recent plot. Metadata is absorbed
            // even with no plot to attach it to, so that a decoration command is
            // never mistaken for the script's result.
            if (isPlotMetadata(value)) {
                if (lastPlot) {
                    applyPlotMetadata(lastPlot, value);
                }
                continue;
            }
            if (isPlotValue(value)) {
                lastPlot = value;
            }
            lastValue = value;
        }
        return {
            value: lastValue,
            display: formatMatlabValue(lastValue),
            type: getMatlabValueType(lastValue),
        };
    }
    const result = matlabEvaluate(ast, execEnv);
    return {
        value: result,
        display: formatMatlabValue(result),
        type: getMatlabValueType(result),
    };
}
/**
 * Create a new MATLAB execution environment
 */
export function createMatlabEnvironment() {
    return new MatlabEnvironment();
}
/**
 * Safely execute MATLAB code and catch errors
 */
export function safeMatlabExecute(code, env) {
    try {
        const result = matlabExecute(code, env);
        return { success: true, result };
    }
    catch (error) {
        if (error instanceof MatlabError) {
            return { success: false, error };
        }
        // Wrap other errors
        return {
            success: false,
            error: new MatlabError(error instanceof Error ? error.message : String(error)),
        };
    }
}
/**
 * Format MATLAB value for display
 */
function flattenArr(v) {
    if (Array.isArray(v))
        return v.flat();
    if (v === null || v === undefined)
        return [];
    return [v];
}
function formatMatlabValue(value) {
    if (value === null)
        return "";
    if (typeof value === "number") {
        if (Number.isInteger(value))
            return String(value);
        return value.toPrecision(6).replace(/\.?0+$/, "");
    }
    if (typeof value === "string")
        return value;
    // Complex number
    if (typeof value === "object" && "re" in value && "im" in value) {
        const re = value.re;
        const im = value.im;
        if (im === 0)
            return formatMatlabValue(re);
        if (re === 0)
            return `${formatMatlabValue(im)}i`;
        const sign = im >= 0 ? "+" : "-";
        return `${formatMatlabValue(re)} ${sign} ${formatMatlabValue(Math.abs(im))}i`;
    }
    // Array or matrix
    if (Array.isArray(value)) {
        if (value.length === 0)
            return "[]";
        // Check if matrix (2D)
        if (Array.isArray(value[0])) {
            const rows = value.map((row) => row.map((v) => formatMatlabValue(v)).join("  "));
            return rows.join("\n");
        }
        // 1D array
        return value.map((v) => formatMatlabValue(v)).join("  ");
    }
    // Function types
    if (typeof value === "object" && "type" in value) {
        const v = value;
        if (v.type === "function" && v.params)
            return `@${v.params.join(",")}`;
        if (v.type === "anonymous" && v.params)
            return `@(${v.params.join(",")})`;
        if (v.type === "handle" && v.name)
            return `@${v.name}`;
        if (v.type === "plot")
            return "[Plot Data]";
    }
    // Data container types (type_ marker)
    if (typeof value === "object" && "type_" in value) {
        const obj = value;
        const t = obj.type_;
        if (t === "table" || t === "timetable") {
            const varNames = obj.Properties
                ?.VariableNames || [];
            if (varNames.length === 0)
                return `0x0 empty ${t}`;
            const cols = varNames.map((n) => ({ name: n, data: flattenArr(obj[n]) }));
            const nRows = Math.max(...cols.map((c) => c.data.length), 0);
            const header = (t === "timetable" ? "    Time          " : "") +
                cols.map((c) => c.name.padStart(10)).join("  ");
            const sep = "-".repeat(header.length);
            const rows = [];
            for (let r = 0; r < nRows; r++) {
                let row = "";
                if (t === "timetable") {
                    const times = flattenArr(obj.RowTimes);
                    row += `    ${String(times[r] ?? "").padEnd(14)}`;
                }
                row += cols
                    .map((c) => {
                    const v = c.data[r];
                    return String(v ?? "NaN").padStart(10);
                })
                    .join("  ");
                rows.push(row);
            }
            return [header, sep, ...rows].join("\n");
        }
        if (t === "datetime") {
            const y = obj.Year, mo = obj.Month, d = obj.Day;
            const h = obj.Hour, mi = obj.Minute, s = obj.Second;
            const months = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
            ];
            if (h || mi || s) {
                return `${String(d).padStart(2, "0")}-${months[mo - 1]}-${y} ${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
            }
            return `${String(d).padStart(2, "0")}-${months[mo - 1]}-${y}`;
        }
        if (t === "duration") {
            const totalSec = obj.totalSeconds_;
            if (totalSec === 0)
                return "0 sec";
            const absS = Math.abs(totalSec);
            const sign = totalSec < 0 ? "-" : "";
            const hrs = Math.floor(absS / 3600);
            const mins = Math.floor((absS % 3600) / 60);
            const secs = absS % 60;
            if (hrs > 0 && mins === 0 && secs === 0)
                return `${sign}${hrs} hr`;
            if (hrs === 0 && mins > 0 && secs === 0)
                return `${sign}${mins} min`;
            if (hrs === 0 && mins === 0)
                return `${sign}${secs} sec`;
            return `${sign}${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs < 10 ? "0" : "")}${secs}`;
        }
        if (t === "categorical") {
            const cats = obj.categories_;
            const data = obj.data_;
            const labels = data.map((i) => i < 0 ? "<undefined>" : (cats[i] ?? "<undefined>"));
            return `categorical: [${labels.join(", ")}]`;
        }
        if (t === "containers.Map") {
            const k = obj.keys_;
            return `  containers.Map with ${k.length} entries`;
        }
        if (t === "NaT")
            return "NaT";
    }
    // Struct or other object
    if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
    }
    return String(value);
}
/**
 * Get MATLAB value type
 */
function getMatlabValueType(value) {
    if (value === null)
        return "null";
    if (typeof value === "number")
        return "double";
    if (typeof value === "string")
        return "char";
    if (typeof value === "object" && "re" in value && "im" in value) {
        return "complex";
    }
    if (Array.isArray(value)) {
        if (value.length === 0)
            return "double";
        if (Array.isArray(value[0])) {
            return `matrix [${value.length}x${value[0].length}]`;
        }
        return `array [1x${value.length}]`;
    }
    if (typeof value === "object" && "type" in value) {
        return value.type;
    }
    if (typeof value === "object" && "type_" in value) {
        return value.type_;
    }
    return "struct";
}
/**
 * Check if a file is a MATLAB script
 */
export function isMatlabFile(filename) {
    return filename.endsWith(".m");
}
/**
 * Parse MATLAB file content
 */
export function parseMatlabFile(content, _filename) {
    const lines = content.split("\n");
    const functions = [];
    let isFunction = false;
    // Check if file starts with function definition
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line === "" || line.startsWith("%"))
            continue;
        if (line.startsWith("function")) {
            isFunction = true;
            // Extract function name - using [^\]]* instead of .*? to prevent polynomial backtracking
            const match = line.match(/function\s+(?:\[[^\]]*\]\s*=\s*)?(\w+)/);
            if (match) {
                functions.push(match[1]);
            }
        }
        break;
    }
    // Find all function definitions - using [^\]]* instead of .*? to prevent polynomial backtracking
    const funcRegex = /^function\s+(?:\[[^\]]*\]\s*=\s*)?(\w+)/gm;
    let match;
    while ((match = funcRegex.exec(content)) !== null) {
        if (!functions.includes(match[1])) {
            functions.push(match[1]);
        }
    }
    return {
        functions,
        mainCode: content,
        isScript: !isFunction,
        isFunction,
    };
}
//# sourceMappingURL=index.js.map